import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Leaf, Check, AlertTriangle, Loader2, Eye, EyeOff } from "lucide-react";
import { apiFetch } from "@/lib/api-fetch";

interface InviteInfo {
  email: string;
  role: string;
  features: string[];
  organization_name: string;
  invited_by_name: string;
  expires_at: string;
}

export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    async function fetchInviteInfo() {
      try {
        const res = await apiFetch(`/api/team/invite/${token}/info`);
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || "This invitation is invalid or has expired.");
          return;
        }
        setInviteInfo(data);
      } catch {
        setError("Unable to load invitation details. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
    fetchInviteInfo();
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError("");

    if (password.length < 8) {
      setSubmitError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/team/invite/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, password, phone: phone.trim() || undefined }),
      });
      const data = await res.json();

      if (!res.ok) {
        setSubmitError(data.error || "Failed to create account. Please try again.");
        return;
      }

      if (data.token) {
        localStorage.setItem("compostdeveloper_token", data.token);
      }
      navigate("/app", { replace: true });
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function formatRole(role: string): string {
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  function formatFeature(feature: string): string {
    return feature
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-forest-500 mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground">Loading invitation...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid or expired token)
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 pt-16">
        <div className="w-full max-w-md">
          <div className="rounded-xl border border-border bg-card p-8 shadow-lg">
            <div className="text-center mb-6">
              <Link to="/" className="inline-flex items-center gap-2 text-foreground hover:opacity-90">
                <img src="/logo.png" alt="CompostDeveloper" className="h-10 w-10 rounded" />
                <span className="text-xl font-bold">
                  Compost<span className="text-forest-500">Developer</span>
                </span>
              </Link>
            </div>
            <div className="flex flex-col items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-400" />
              </div>
              <h1 className="text-xl font-semibold text-foreground">Invitation Invalid</h1>
              <p className="text-sm text-muted-foreground text-center">{error}</p>
              <Link
                to="/login"
                className="mt-2 inline-block px-6 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 pt-16 pb-8">
      <div className="w-full max-w-md">
        <div className="rounded-xl border border-border bg-card p-8 shadow-lg">
          {/* Logo */}
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex items-center gap-2 text-foreground hover:opacity-90">
              <img src="/logo.png" alt="CompostDeveloper" className="h-10 w-10 rounded" />
              <span className="text-xl font-bold">
                Compost<span className="text-forest-500">Developer</span>
              </span>
            </Link>
          </div>

          {/* Invitation header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-foreground">
              You've been invited to join
            </h1>
            <p className="mt-1 text-lg font-medium text-forest-500">
              {inviteInfo!.organization_name}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Invited by {inviteInfo!.invited_by_name}
            </p>
          </div>

          {/* Role and features */}
          <div className="mb-6 rounded-lg bg-secondary border border-border p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Your Role
              </span>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-forest-500/10 text-forest-500 border border-forest-500/20">
                {formatRole(inviteInfo!.role)}
              </span>
            </div>
            {inviteInfo!.features && inviteInfo!.features.length > 0 && (
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Access
                </span>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {inviteInfo!.features.map((feature) => (
                    <span
                      key={feature}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-forest-500/10 text-forest-400 border border-forest-500/20"
                    >
                      <Check className="h-3 w-3" />
                      {formatFeature(feature)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {submitError && (
              <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
                {submitError}
              </div>
            )}

            {/* Email (read-only) */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={inviteInfo!.email}
                disabled
                className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-muted-foreground text-sm cursor-not-allowed opacity-70"
              />
            </div>

            {/* Full Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1.5">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 focus:border-transparent text-sm"
                placeholder="Your full name"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1.5">
                Phone Number <span className="text-muted-foreground text-xs">(optional, for SMS notifications)</span>
              </label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 focus:border-transparent text-sm"
                placeholder="(602) 555-0142"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-11 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 focus:border-transparent text-sm"
                  placeholder="Minimum 8 characters"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-1.5">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 focus:border-transparent text-sm"
                placeholder="Re-enter your password"
                minLength={8}
                required
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 px-4 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
            >
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating Account...
                </span>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-forest-500 hover:text-forest-400 font-medium">
              Sign in
            </Link>
          </p>
        </div>
        <p className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            &larr; Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
